import { jsPDF } from 'jspdf';

// Business-style PDF report (A4, portrait): cover header, executive summary, one section per server with the current
// server vs the recommendation, workload metrics, alternatives and notes. Drawn by hand (no autotable dependency).
const C = {
  navy: [35, 47, 62], ink: [22, 30, 45], orange: [236, 114, 17], blue: [0, 115, 187], text: [31, 41, 55],
  muted: [100, 112, 128], line: [221, 226, 233], band: [244, 246, 249], green: [29, 129, 2], red: [209, 50, 18], white: [255, 255, 255],
};
const W = 210, H = 297, M = 16, CW = W - 2 * M;

const money = (v) => (v == null || Number.isNaN(Number(v)) ? 'Price unavailable' : `$${Math.round(Number(v)).toLocaleString('en-US')}`);
const num = (v, d = 0) => (v == null || Number.isNaN(Number(v)) ? '-' : Number(v).toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d }));
const pct = (v) => (v == null || Number.isNaN(Number(v)) ? '-' : `${Number(v).toFixed(1)}%`);

export function buildReport({ results, customerName }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const name = (customerName || '').trim() || 'SSAT Analysis';
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  let y = 0;

  const fill = (c) => doc.setFillColor(...c);
  const ink = (c) => doc.setTextColor(...c);
  const stroke = (c) => doc.setDrawColor(...c);
  const font = (size, style = 'normal', color = C.text) => { doc.setFont('helvetica', style); doc.setFontSize(size); ink(color); };
  const ensure = (need) => { if (y + need > H - 18) { doc.addPage(); y = 20; } };
  const wrap = (text, width) => doc.splitTextToSize(String(text || ''), width);

  // ---------- cover header
  fill(C.navy); doc.rect(0, 0, W, 52, 'F');
  fill(C.orange); doc.rect(0, 52, W, 2, 'F');
  font(9, 'bold', C.orange); doc.text('SQL SERVER SIZING ASSESSMENT', M, 17);
  font(22, 'bold', C.white); doc.text(wrap(name, CW)[0], M, 29);
  font(11, 'normal', [200, 210, 222]); doc.text('Amazon RDS for SQL Server recommendation', M, 37);
  font(9, 'normal', [160, 174, 192]); doc.text(`Prepared ${date}`, M, 45);
  y = 66;

  const servers = results.batchMode
    ? (results.results || []).filter((r) => r.success).map((r) => ({ analysis: r.analysis, recs: r.recommendations, name: r.serverName || r.fileName }))
    : [{ analysis: results.analysis, recs: results.recommendations, name: results.serverName }];

  // ---------- executive summary
  const total = servers.reduce((s, x) => s + (Number(x.recs?.[0]?.monthlyPrice) || 0), 0);
  const priced = servers.filter((x) => x.recs?.[0]?.monthlyPrice != null).length;
  const tiles = [
    ['Servers assessed', String(servers.length)],
    ['Estimated monthly cost', priced ? money(total) : 'Price unavailable'],
    ['Estimated annual cost', priced ? money(total * 12) : 'Price unavailable'],
  ];
  const tw = (CW - 8) / 3;
  tiles.forEach(([label, value], i) => {
    const x = M + i * (tw + 4);
    fill(C.band); stroke(C.line); doc.roundedRect(x, y, tw, 22, 2, 2, 'FD');
    fill(i === 1 ? C.orange : C.blue); doc.rect(x, y + 3, 1.2, 16, 'F');
    font(8, 'bold', C.muted); doc.text(label.toUpperCase(), x + 5, y + 8);
    font(15, 'bold', C.ink); doc.text(value, x + 5, y + 17);
  });
  y += 32;

  if (servers.length > 1) {
    font(12, 'bold', C.ink); doc.text('Summary by server', M, y); y += 5;
    const cols = [['Server', M + 2], ['Current', M + 58], ['Recommended', M + 90], ['Per month', M + CW - 2]];
    fill(C.navy); doc.rect(M, y, CW, 8, 'F'); font(8.5, 'bold', C.white);
    cols.forEach(([t, x], i) => doc.text(t, x, y + 5.4, i === 3 ? { align: 'right' } : undefined)); y += 8;
    servers.forEach((s, i) => {
      ensure(8); if (i % 2) { fill(C.band); doc.rect(M, y, CW, 8, 'F'); }
      const cur = s.analysis?.currentServer; const rec = s.recs?.[0];
      font(8.5, 'normal', C.text);
      doc.text(wrap(s.name || 'Server', 52)[0], M + 2, y + 5.4);
      doc.text(`${cur?.cores ?? '?'} cores / ${cur?.memoryGB ?? '?'} GB`, M + 58, y + 5.4);
      font(8.5, 'bold', C.ink); doc.text(rec?.instanceType || '-', M + 90, y + 5.4);
      font(8.5, 'normal', C.text); doc.text(money(rec?.monthlyPrice), M + CW - 2, y + 5.4, { align: 'right' }); y += 8;
    });
    stroke(C.line); doc.line(M, y, M + CW, y); y += 10;
  }

  // ---------- one section per server
  const section = (title) => { // coloured section heading
    ensure(14); font(9, 'bold', C.blue); doc.text(title.toUpperCase(), M, y); stroke(C.line); doc.line(M, y + 1.8, M + CW, y + 1.8); y += 7;
  };
  const kv = (x, w, label, value) => {
    font(8.5, 'normal', C.muted); doc.text(label, x, y);
    font(9, 'bold', C.ink); doc.text(String(value), x + w, y, { align: 'right' });
  };

  servers.forEach((s, idx) => {
    if (idx > 0 || servers.length > 1 || y > 170) { doc.addPage(); y = 20; }   // batch: summary page first, then one page per server
    const a = s.analysis || {}; const cur = a.currentServer || {}; const rec = s.recs?.[0];
    // server title bar
    fill(C.navy); doc.roundedRect(M, y, CW, 11, 1.5, 1.5, 'F'); fill(C.orange); doc.rect(M, y, 2.2, 11, 'F');
    font(12, 'bold', C.white); doc.text(wrap(s.name || 'Server', CW - 12)[0], M + 6, y + 7.4); y += 18;

    // current vs recommended cards
    const half = (CW - 6) / 2; const top = y;
    fill(C.band); stroke(C.line); doc.roundedRect(M, top, half, 44, 2, 2, 'FD');
    doc.roundedRect(M + half + 6, top, half, 44, 2, 2, 'FD');
    fill(C.orange); doc.rect(M + half + 6, top, half, 1.4, 'F');
    font(8, 'bold', C.muted); doc.text('CURRENT SERVER', M + 4, top + 7); doc.text('RECOMMENDED', M + half + 10, top + 7);
    y = top + 14;
    [['CPU cores', cur.cores ?? '?'], ['Memory', `${cur.memoryGB ?? '?'} GB`], ['SQL Server edition', String(cur.sqlEdition || '-').replace(' Edition', '')], ['SQL Server version', cur.sqlVersion || '-']]
      .forEach(([k, v]) => { kv(M + 4, half - 8, k, v); y += 7; });
    y = top + 14;
    if (rec) {
      font(14, 'bold', C.ink); doc.text(rec.instanceType, M + half + 10, y); y += 7;
      [['vCPU / memory', `${rec.specs?.cpu ?? '?'} / ${rec.specs?.memory ?? '?'} GB`], ['Storage', (rec.ebsType || 'N/A').toUpperCase()], ['Estimated monthly', money(rec.monthlyPrice)]]
        .forEach(([k, v]) => { kv(M + half + 10, half - 8, k, v); y += 7; });
    } else { font(9, 'normal', C.muted); doc.text('No recommendation available', M + half + 10, y); }
    y = top + 52;

    // workload metrics
    section('Workload observed');
    const head = ['Metric', 'Average', 'P95 (used for sizing)', 'Maximum'];
    const xs = [M + 2, M + 80, M + 130, M + CW - 2];
    fill(C.navy); doc.rect(M, y, CW, 7.5, 'F'); font(8, 'bold', C.white);
    head.forEach((t, i) => doc.text(t, xs[i], y + 5, i ? { align: 'right' } : undefined)); y += 7.5;
    const rows = [
      ['CPU utilisation', pct(a.cpu?.avg), pct(a.cpu?.p95), pct(a.cpu?.max)],
      ['Memory pressure', pct(a.memory?.avg), pct(a.memory?.p95), pct(a.memory?.max)],
      ['IOPS', num(a.iops?.avg), num(a.iops?.p95), num(a.iops?.max)],
      ['Throughput (MB/s)', num(a.throughput?.avg), num(a.throughput?.p95), num(a.throughput?.max)],
    ];
    rows.forEach((r, i) => {
      if (i % 2) { fill(C.band); doc.rect(M, y, CW, 7.5, 'F'); }
      font(8.5, 'normal', C.text); doc.text(r[0], xs[0], y + 5);
      r.slice(1).forEach((v, j) => { font(8.5, j === 1 ? 'bold' : 'normal', j === 1 ? C.ink : C.text); doc.text(v, xs[j + 1], y + 5, { align: 'right' }); });
      y += 7.5;
    });
    const cp = a.collectionPeriod;
    if (cp?.hours) { font(8, 'normal', C.muted); doc.text(`Collection period: ${num(cp.hours)} hours - data confidence ${cp.confidence || 'UNKNOWN'}`, M, y + 5); y += 6; }
    y += 6;

    // why this recommendation
    if (rec?.explanation) {
      section('Why this recommendation');
      font(9, 'normal', C.text);
      const lines = wrap(rec.explanation, CW - 6);
      lines.forEach((ln) => { ensure(5); doc.text(ln, M + 3, y); y += 4.6; });
      if (rec.liftAndShiftSize) { ensure(8); font(8.5, 'normal', C.muted); doc.text(`Like-for-like (lift and shift) size for reference: ${rec.liftAndShiftSize}`, M + 3, y + 1); y += 6; }
      y += 4;
    }

    // alternatives
    const alts = (s.recs || []).slice(1);
    if (alts.length) {
      section('Alternatives');
      alts.forEach((r, i) => {
        ensure(8); if (i % 2) { fill(C.band); doc.rect(M, y - 1, CW, 7.5, 'F'); }
        font(8.5, 'bold', C.ink); doc.text(r.instanceType, M + 2, y + 4);
        font(8.5, 'normal', C.text); doc.text(`${r.specs?.cpu ?? '?'} vCPU / ${r.specs?.memory ?? '?'} GB`, M + 70, y + 4);
        doc.text(money(r.monthlyPrice), M + CW - 2, y + 4, { align: 'right' }); y += 7.5;
      });
      y += 4;
    }

    // warnings
    const warnings = [...(rec?.warnings || []), ...(a.warnings || [])].filter(Boolean);
    if (warnings.length) {
      section('Notes');
      warnings.forEach((w) => { wrap(`- ${w}`, CW - 6).forEach((ln) => { ensure(5); font(9, 'normal', C.red); doc.text(ln, M + 3, y); y += 4.6; }); });
    }
  });

  // ---------- footer on every page
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p); stroke(C.line); doc.line(M, H - 12, W - M, H - 12);
    font(8, 'normal', C.muted); doc.text(`${name} - SQL Server sizing assessment`, M, H - 7);
    doc.text(`Page ${p} of ${pages}`, W - M, H - 7, { align: 'right' });
  }
  const fileName = `${name.replace(/[^\w.-]+/g, '_')}-report-${new Date().toISOString().split('T')[0]}.pdf`;
  return { doc, fileName };
}
